import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'

import styles from './field.module.scss'

const zahl = n => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.')

/**
 * Ein Feld des Abgaberasters.
 *
 * Geschrieben wird laufend: 800 ms nach dem letzten Tastendruck geht der Text
 * hinaus, zusätzlich beim Verlassen des Feldes. Ein Speichern-Knopf würde hier
 * nur dazu führen, dass Arbeit verloren geht.
 *
 * Die Längenangabe ist ein **Richtwert**, keine Grenze — so steht es im
 * Abgaberaster. Das Feld sperrt nicht; der Zähler wird orange und sagt es.
 *
 * Ist das Konzept abgeschlossen oder bewertet, ist das Feld nur noch lesbar.
 * Die Punktzahl der Lehrkraft steht dann darüber; sie lässt sich hier nicht
 * verändern.
 */
export default function ConceptField({ field, students, readOnly, onSave, onAssign }) {
	const [text, setText] = useState(field.text || '')
	const [stand, setStand] = useState('gespeichert')
	const timer = useRef(null)
	const letzterServerwert = useRef(field.text || '')

	// Kommt von außen ein anderer Text (nach dem Zurückgeben, oder weil eine
	// andere Person geschrieben hat), wird er übernommen — aber nur, wenn er
	// sich wirklich geändert hat. Sonst überschriebe jede Antwort des Servers
	// die gerade getippten Zeichen.
	useEffect(() => {
		if (field.text !== letzterServerwert.current) {
			letzterServerwert.current = field.text || ''
			setText(field.text || '')
		}
	}, [field.text])

	useEffect(() => () => clearTimeout(timer.current), [])

	const speichern = wert => {
		setStand('speichert')
		Promise.resolve(onSave(field.no, wert))
			.then(() => {
				letzterServerwert.current = wert
				setStand('gespeichert')
			})
			.catch(() => setStand('fehler'))
	}

	const tippen = event => {
		const wert = event.target.value
		setText(wert)
		setStand('offen')
		clearTimeout(timer.current)
		timer.current = setTimeout(() => speichern(wert), 800)
	}

	const verlassen = () => {
		clearTimeout(timer.current)
		if (text !== letzterServerwert.current) speichern(text)
	}

	const zuviel = text.length > field.limit
	const zugeteilt = new Set(field.assigned || [])

	const umschalten = id => {
		const naechste = new Set(zugeteilt)
		if (naechste.has(id)) naechste.delete(id)
		else naechste.add(id)
		onAssign(field.no, [...naechste])
	}

	return (
		<article className={clsx(styles.root, readOnly && styles.readOnly)} id={`feld-${field.no}`}>
			<header className={styles.head}>
				<span className={styles.no}>{field.no}</span>
				<h4>{field.title}</h4>
				{field.score !== null && field.score !== undefined ? (
					<span className={styles.score}>{field.score} / 10 Punkte</span>
				) : (
					<span className={styles.limit}>Richtwert {zahl(field.limit)} Zeichen</span>
				)}
			</header>

			<p className={styles.question}>{field.question}</p>

			{readOnly ? (
				<div className={styles.readText}>{text || 'Noch nicht ausgefüllt.'}</div>
			) : (
				<textarea
					className={styles.input}
					value={text}
					rows={6}
					placeholder="Hier schreibt ihr euer Konzept. Der Zwischenstand wird automatisch gespeichert."
					onChange={tippen}
					onBlur={verlassen}
				/>
			)}

			<div className={styles.foot}>
				<span className={styles[stand === 'fehler' ? 'stateError' : 'state']}>
					{readOnly
						? 'Abgeschlossen — nur noch lesbar'
						: stand === 'gespeichert'
						? 'Gespeichert'
						: stand === 'speichert'
						? 'Wird gespeichert …'
						: stand === 'fehler'
						? 'Konnte nicht gespeichert werden'
						: 'Nicht gespeichert'}
				</span>
				<span className={clsx(styles.counter, zuviel && styles.over)}>
					{zahl(text.length)} / {zahl(field.limit)}
				</span>
			</div>

			<div className={styles.assign}>
				<span className={styles.assignLabel}>Wer hat daran gearbeitet?</span>
				<div className={styles.chips}>
					{students.map(student => (
						<button
							key={student.id}
							type="button"
							disabled={readOnly}
							aria-pressed={zugeteilt.has(student.id)}
							className={clsx(styles.chip, zugeteilt.has(student.id) && styles.chipOn)}
							onClick={() => umschalten(student.id)}
						>
							{student.name}
						</button>
					))}
				</div>
				{!readOnly && students.length > 0 && (
					<div className={styles.assignActions}>
						<button
							type="button"
							className={styles.linkBtn}
							onClick={() =>
								onAssign(
									field.no,
									students.map(s => s.id)
								)
							}
						>
							alle übernehmen
						</button>
						<button type="button" className={styles.linkBtn} onClick={() => onAssign(field.no, [])}>
							alle wegnehmen
						</button>
					</div>
				)}
			</div>
		</article>
	)
}

ConceptField.propTypes = {
	field: PropTypes.object.isRequired,
	students: PropTypes.array.isRequired,
	readOnly: PropTypes.bool,
	onSave: PropTypes.func.isRequired,
	onAssign: PropTypes.func.isRequired
}

ConceptField.defaultProps = {
	readOnly: false
}
